import * as ToastPrimitive from '@radix-ui/react-toast';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

import { ToastContext, type ToastItem, type ToastVariant } from './ToastContext.js';

const VARIANT_STYLES: Record<ToastVariant, string> = {
  success: 'border-status-success bg-status-success-bg',
  error: 'border-status-error bg-status-error-bg',
  warning: 'border-status-warning bg-status-warning-bg',
  info: 'border-status-info bg-status-info-bg',
};

const VARIANT_ICONS: Record<ToastVariant, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

function ToastItem({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const Icon = VARIANT_ICONS[item.variant];

  return (
    <ToastPrimitive.Root
      className={cn(
        'flex items-start gap-3 rounded-lg border-l-4 p-4 shadow-md',
        VARIANT_STYLES[item.variant],
      )}
      duration={item.duration ?? 5000}
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />

      <div className="flex-1">
        <ToastPrimitive.Title className="text-sm font-semibold text-gray-900">
          {item.title}
        </ToastPrimitive.Title>
        {item.description && (
          <ToastPrimitive.Description className="mt-1 text-sm text-gray-600">
            {item.description}
          </ToastPrimitive.Description>
        )}
        {item.action && (
          <ToastPrimitive.Action
            altText={item.action.label}
            className="mt-2 text-sm font-medium text-brand-600 hover:text-brand-700 underline"
            onClick={item.action.onClick}
          >
            {item.action.label}
          </ToastPrimitive.Action>
        )}
      </div>

      <ToastPrimitive.Close
        className="min-h-[44px] min-w-[44px] rounded-sm p-1 text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 inline-flex items-center justify-center"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const toast = useCallback((item: Omit<ToastItem, 'id'>) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { ...item, id }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      <ToastPrimitive.Provider>
        {children}
        {toasts.map((item) => (
          <ToastItem key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
        ))}
        <ToastPrimitive.Viewport
          className="fixed bottom-4 right-4 z-toast flex w-full max-w-sm flex-col gap-2"
          role="status"
          aria-live="polite"
          aria-label="Notifications"
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
